/** @jsx jsx */
import { useContext } from "react"
import { jsx, useThemeUI } from "theme-ui"
import PropTypes from "prop-types"

import ModeContext from "../context/mode-context"
import Icon from "../assets/icon"
import { getButtonTokens, buttonColorSx } from "./button-variants"

// Sizes and recolors the (differently-authored) icon SVGs via the wrapper, since
// several icon components don't apply width/height/fill from their `style` prop.
const iconWrapperSx = {
  display: "inline-flex",
  flex: "0 0 auto",
  width: "1em",
  height: "1em",
  "& svg": {
    width: "100%",
    height: "100%",
    display: "block",
  },
}

const renderIcon = (name) =>
  name ? (
    <span sx={iconWrapperSx} aria-hidden="true">
      <Icon icon={name} style={{ fill: "currentColor" }} />
    </span>
  ) : null

/**
 * Label plus optional icon slots (DEV-744/745). Without icons it is the plain
 * text, so buttons that set none render exactly as before.
 */
const ButtonContent = ({ text, iconLeft, iconRight }) =>
  iconLeft || iconRight ? (
    <span
      sx={{
        display: "inline-flex",
        // An inline-flex box takes its baseline from its first item; with a
        // leading icon that is the icon's bottom edge, which grows the line
        // box. Top-aligning keeps every button at the same height.
        verticalAlign: "top",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      {renderIcon(iconLeft)}
      <span>{text}</span>
      {renderIcon(iconRight)}
    </span>
  ) : (
    text
  )

ButtonContent.propTypes = {
  text: PropTypes.node,
  iconLeft: PropTypes.string,
  iconRight: PropTypes.string,
}

/**
 * Color styles (bg, text, border, hover, focus) of a button variant in the
 * active business-unit color. `surface` says what the button sits on — see
 * getButtonTokens.
 */
export const useButtonColorSx = ({ variant, mode, surface }) => {
  const { mode: globalMode } = useContext(ModeContext)
  const { rawColors: colors } = useThemeUI().theme
  const modeKey = mode || globalMode
  const businessUnitColor =
    colors.modes[modeKey]?.primary ?? colors.modes["group"].primary
  return buttonColorSx(getButtonTokens({ variant, businessUnitColor, surface }))
}

export default ButtonContent
